import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import {
  ShieldAlert,
  KeyRound,
  MailCheck,
  RotateCcw,
  Search,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const PAGE = 50;

// Event-type metadata — icon + tone + label. Keep in sync with backend
// EVENT_TYPES tuple in routes/security_events.py.
const TYPE_META = {
  login_failed:            { label: "Failed Login",       Icon: ShieldAlert, tone: "red" },
  password_reset_request:  { label: "Reset Requested",    Icon: KeyRound,    tone: "amber" },
  password_reset_confirm:  { label: "Password Reset",     Icon: KeyRound,    tone: "green" },
  invite_sent:             { label: "Invite Sent",        Icon: MailCheck,   tone: "blue" },
  invite_resent:           { label: "Invite Resent",      Icon: RotateCcw,   tone: "blue" },
};

const TYPE_OPTIONS = Object.entries(TYPE_META).map(([value, m]) => ({
  value,
  label: m.label,
}));

const SUCCESS_OPTIONS = [
  { value: "true", label: "Success only" },
  { value: "false", label: "Failed only" },
];

export default function SecurityLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState([]);
  const [successFilter, setSuccessFilter] = useState([]);
  const [emailQ, setEmailQ] = useState("");
  const [days, setDays] = useState(30);
  const [expanded, setExpanded] = useState(null);

  const params = useMemo(() => {
    const p = { limit: PAGE, offset, days };
    if (typeFilter.length === 1) p.event_type = typeFilter[0];
    if (successFilter.length === 1) p.success = successFilter[0];
    if (emailQ.trim()) p.target_email = emailQ.trim();
    return p;
  }, [offset, days, typeFilter, successFilter, emailQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/security-events", { params });
      let items = r.data.items || [];
      // Client-side multi-select fallback when > 1 type selected.
      if (typeFilter.length > 1) {
        items = items.filter((i) => typeFilter.includes(i.event_type));
      }
      setRows(items);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load security events"));
    } finally { setLoading(false); }
  }, [params, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Aggregate — failed logins per email in the current window (top offenders).
  const topOffenders = useMemo(() => {
    const m = {};
    rows.filter((r) => r.event_type === "login_failed").forEach((r) => {
      const k = r.target_email || "(unknown)";
      m[k] = (m[k] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [rows]);
  const failedTotal = useMemo(
    () => rows.filter((r) => !r.success).length,
    [rows],
  );

  return (
    <div>
      <PageHeader
        title="Security Log"
        subtitle="Failed logins, password-reset activity, and invite send/resend events. Retained for 365 days. Admin-only."
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Events (in window)</div>
            <div className="font-display text-2xl font-black text-white" data-testid="security-total">{total}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Failures on page</div>
            <div className="font-display text-2xl font-black text-[#FF3B30]" data-testid="security-failed-total">{failedTotal}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3 col-span-2">
            <div className="label-mute mb-2">Top failed-login targets (on page)</div>
            {topOffenders.length === 0 ? (
              <div className="text-zinc-500 text-xs italic">No failed logins in this window.</div>
            ) : (
              <div className="space-y-1" data-testid="security-top-offenders">
                {topOffenders.map(([email, n]) => (
                  <div key={email} className="flex items-center justify-between text-xs font-mono">
                    <span className="truncate">{email}</span>
                    <span className="text-[#FF3B30] font-bold">{n}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3" data-testid="security-filters">
          <div className="flex-1 min-w-[220px] max-w-sm">
            <div className="label-mute mb-1">Target email</div>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="security-email"
                className={`${inputClass} pl-10`}
                placeholder="jdoe@company.com"
                value={emailQ}
                onChange={(e) => { setEmailQ(e.target.value); setOffset(0); }}
              />
            </div>
          </div>
          <div>
            <div className="label-mute mb-1">Event type</div>
            <MultiSelectDropdown
              testId="security-type-filter"
              label="All events"
              options={TYPE_OPTIONS}
              selected={typeFilter}
              onChange={(v) => { setTypeFilter(v); setOffset(0); }}
              triggerClassName="min-w-[190px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Outcome</div>
            <MultiSelectDropdown
              testId="security-success-filter"
              label="All outcomes"
              options={SUCCESS_OPTIONS}
              selected={successFilter}
              onChange={(v) => { setSuccessFilter(v.slice(-1)); setOffset(0); }}
              triggerClassName="min-w-[150px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Window (days)</div>
            <select
              data-testid="security-days"
              className={inputClass}
              value={days}
              onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }}
            >
              {[1, 7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="ml-auto text-xs font-mono text-zinc-500 pb-2" data-testid="security-pagination">
            {rows.length === 0 ? "0" : `${offset + 1}–${Math.min(offset + rows.length, total)} of ${total}`}
          </div>
          <div className="inline-flex gap-1 pb-1">
            <GhostButton data-testid="security-prev" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>← Prev</GhostButton>
            <GhostButton data-testid="security-next" disabled={offset + rows.length >= total} onClick={() => setOffset((o) => o + PAGE)}>Next →</GhostButton>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="security-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-4 py-3 text-left w-4"></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">When</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Event</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Target</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Actor</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Outcome</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Reason</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={7} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16 text-zinc-500 scanlines">No security events match the filters.</td></tr>
              )}
              {rows.map((r) => {
                const meta = TYPE_META[r.event_type] || { label: r.event_type, Icon: ShieldAlert, tone: "default" };
                const Icon = meta.Icon;
                const isOpen = expanded === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr className="hover:bg-[#1c1c1e] align-top" data-testid={`security-row-${r.id}`}>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          data-testid={`security-toggle-${r.id}`}
                          className="text-zinc-500 hover:text-white"
                          title={isOpen ? "Hide details" : "Show details"}
                        >
                          {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-zinc-400 text-xs whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-xs">
                          <Icon className="size-3.5" />
                          <PillBadge tone={meta.tone}>{meta.label}</PillBadge>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-200 text-xs">{r.target_email || <span className="text-zinc-600">—</span>}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">
                        {r.actor_email ? (
                          <>
                            {r.actor_name || r.actor_email}
                            {r.actor_name && <div className="text-[10px] text-zinc-600 font-mono">{r.actor_email}</div>}
                          </>
                        ) : <span className="text-zinc-600 italic">(system)</span>}
                      </td>
                      <td className="px-4 py-3">
                        <PillBadge tone={r.success ? "green" : "red"}>{r.success ? "success" : "failure"}</PillBadge>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{r.reason || "—"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#0A0A0A]" data-testid={`security-details-${r.id}`}>
                        <td></td>
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono text-zinc-400">
                            <div>
                              <div className="label-mute">IP address</div>
                              <div className="mt-1 text-zinc-200 break-all">{r.ip || "—"}</div>
                            </div>
                            <div>
                              <div className="label-mute">User agent</div>
                              <div className="mt-1 text-zinc-200 break-all">{r.user_agent || "—"}</div>
                            </div>
                            <div>
                              <div className="label-mute">Meta</div>
                              <pre className="mt-1 whitespace-pre-wrap text-zinc-200 text-[10px]">
{r.meta ? JSON.stringify(r.meta, null, 2) : "—"}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
